import { useId } from 'react';

import ActionButton from '@/components/Button/ActionButton';
import Input from '@/components/Input/Input';
import { HStack, VStack } from '@/components/Layout';
import { Text } from '@/components/Typography';
import { Separator } from '@/components/ui/separator';
import type { EmployeeSubIdListItem } from '@/features/section-attribute/types';
import { useCommonTranslation } from '@/hooks/useCommonTranslation';
import { defaultNS, useTranslation } from '@/lib/i18n';

import {
  CFormItemBase,
  type CFormItemProps,
  buildCFormItem,
} from '../CFormItem';

import { MasterDataCombobox } from './MasterDataCombobox';

type Props = CFormItemProps & {
  value?: EmployeeSubIdListItem[];
  onChange: (value: EmployeeSubIdListItem[]) => void;
};

/**
 * 従業員サブIDのリストを入力する CFormItem
 */
export const CFormEmployeeSubIdListItem = buildCFormItem((props: Props) => {
  const tc = useCommonTranslation();
  const { t } = useTranslation(defaultNS, {
    keyPrefix: 'features.cForm.employeeSubIdItem',
  });
  const idPrefix = useId();
  const values = props.value || [];

  const handleAdd = () => {
    props.onChange([
      ...values,
      { employeeSubIdNameId: null, employeeSubIdName: null, employeeSubId: '' },
    ]);
  };

  const handleRemove = (index: number) => {
    const newValues = [...values];
    newValues.splice(index, 1);
    props.onChange(newValues);
  };

  const handleChange = (
    index: number,
    field: keyof EmployeeSubIdListItem,
    value: string,
    name?: string,
  ) => {
    const newValues = [...values];
    if (field === 'employeeSubIdNameId') {
      newValues[index] = {
        ...newValues[index],
        employeeSubIdNameId: value,
        employeeSubIdName: name || '',
      };
    } else {
      newValues[index] = { ...newValues[index], [field]: value };
    }
    props.onChange(newValues);
  };

  const renderReadMode = () => {
    return (
      <VStack className="gap-2 w-full">
        <HStack className="items-center w-full gap-4">
          <div className="flex-1">
            <Text variant="tableHead">{t('employeeSubIdName')}</Text>
          </div>
          <div className="flex-1">
            <Text variant="tableHead">{t('employeeSubId')}</Text>
          </div>
        </HStack>
        <Separator className="w-full" />
        {values.length === 0 ? (
          <Text variant="p" className="text-center text-neutral-muted-on-fill">
            {t('unregistered')}
          </Text>
        ) : (
          <>
            {values.map((item, index) => (
              <VStack key={`${idPrefix}-${index}`} className="gap-2 w-full">
                <HStack className="items-center w-full gap-4">
                  <div className="flex-1">
                    <Text variant="p">{item.employeeSubIdName || '-'}</Text>
                  </div>
                  <div className="flex-1">
                    <Text variant="p">{item.employeeSubId || '-'}</Text>
                  </div>
                </HStack>
                {index < values.length - 1 && <Separator className="w-full" />}
              </VStack>
            ))}
          </>
        )}
      </VStack>
    );
  };

  const renderWriteMode = () => {
    return (
      <VStack className="gap-2 w-full">
        <HStack className="items-center w-full gap-4">
          <div className="flex-1 justify-start">
            <Text variant="tableHead">{t('employeeSubIdName')}</Text>
          </div>
          <div className="flex-1 justify-start">
            <Text variant="tableHead">{t('employeeSubId')}</Text>
          </div>
          <div className="w-10"></div>
        </HStack>
        <Separator className="w-full" />

        {values.map((item, index) => (
          <VStack key={`${idPrefix}-${index}`} className="gap-2 w-full">
            <HStack className="items-center w-full gap-4">
              <div className="flex-1 justify-start">
                <MasterDataCombobox
                  value={item.employeeSubIdNameId ?? undefined}
                  displayValue={item.employeeSubIdName ?? undefined}
                  onChange={(id, name) =>
                    handleChange(index, 'employeeSubIdNameId', id || '', name)
                  }
                  apiPath="/v2/employee-sub-id-names"
                  queryKeyPrefix="employeeSubIdNames"
                  disabled={props.readonly}
                />
              </div>
              <div className="flex-1 justify-start">
                <Input
                  value={item.employeeSubId}
                  onChange={(e) =>
                    handleChange(index, 'employeeSubId', e.target.value)
                  }
                  disabled={props.readonly}
                />
              </div>
              <div className="w-10">
                <ActionButton
                  icon="Trash2Icon"
                  variant="ghost"
                  iconColor="muted"
                  onClick={() => handleRemove(index)}
                  disabled={props.readonly}
                  aria-label={tc('action.delete')}
                />
              </div>
            </HStack>
            <Separator className="w-full" />
          </VStack>
        ))}

        {!props.readonly && (
          <HStack className="items-left">
            <ActionButton
              className="pl-0"
              icon="PlusIcon"
              onClick={handleAdd}
              variant="ghost"
              iconColor="primary"
            >
              {t('addEmployeeSubIdName')}
            </ActionButton>
          </HStack>
        )}
      </VStack>
    );
  };

  return (
    <CFormItemBase
      {...props}
      readComponent={renderReadMode()}
      writeComponent={renderWriteMode()}
    />
  );
});
